/**
 * MissedOrderCorrectionControl — the one-click "That is wrong" on a Merlin
 * missed-order finding (kanban fc6bca28, 10/27 Alpha: Merlin learns from
 * human corrections over time).
 *
 * Opens a small inline form: WHICH part of the verdict is wrong (Merlin's
 * cause, subcategory, responsible party, miss / not a miss, or PJ's
 * category), WHAT it should be (the vocabulary comes from the API, never
 * retyped here) and WHY (free text, required: the "because Y" is the
 * lesson). PJ's category is the source of truth, so that choice is labelled
 * as a challenge routed to PJ, never an overwrite. The finding row itself is
 * never edited; the correction is its own audited row.
 *
 * The form is rendered through a portal with fixed positioning anchored to
 * the trigger button: the findings table scrolls horizontally on narrow
 * screens and would clip an in-cell popover.
 *
 * Every control carries a data-action for the shared recorder; the submit
 * also records a trackEvent with the field name (never the reason text).
 */
import { useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { EDGE_GAP, POPOVER_WIDTH, popoverStyle } from './correctionPopover';
import {
  correctionErrorMessage,
  portalMissedOrdersService,
  type CorrectionField,
  type CorrectionOptions,
  type MissedOrderCorrection,
  type MissedOrderFindingRow,
  type VocabOption,
} from '../../services/portalMissedOrders';
import { trackEvent } from '../../services/portalTracking';
import styles from './MissedOrderCorrectionControl.module.css';

interface Props {
  finding: MissedOrderFindingRow;
  options: CorrectionOptions;
  onRecorded: (correction: MissedOrderCorrection) => void;
}

/** What the finding currently says for a field, as shown next to "Merlin said". */
function currentValueFor(finding: MissedOrderFindingRow, field: CorrectionField): string {
  switch (field) {
    case 'cause_category':
      return finding.cause_category || '—';
    case 'merlin_subcategory':
      return finding.merlin_subcategory || '(blank)';
    case 'responsible_party':
      return finding.responsible_party || '—';
    case 'is_miss':
      return finding.is_miss == null ? 'undecided' : finding.is_miss ? 'a miss' : 'not a miss';
    case 'pj_category':
      return finding.pj_grade || finding.pj_reason || '(no PJ grade yet)';
  }
}

function choicesFor(options: CorrectionOptions, field: CorrectionField): VocabOption[] | null {
  switch (field) {
    case 'cause_category':
      return options.cause_categories;
    case 'responsible_party':
      return options.responsible_parties;
    case 'is_miss':
      return options.is_miss;
    case 'pj_category':
      return options.pj_categories;
    default:
      return null;
  }
}

export default function MissedOrderCorrectionControl({ finding, options, onRecorded }: Props) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [style, setStyle] = useState<CSSProperties>({ position: 'fixed', top: 0, right: EDGE_GAP, width: POPOVER_WIDTH });

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const el = triggerRef.current;
      if (!el) return;
      setStyle(popoverStyle(el.getBoundingClientRect(), window.innerWidth));
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);
  const [field, setField] = useState<CorrectionField>('cause_category');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const choices = useMemo(() => choicesFor(options, field), [options, field]);
  const current = currentValueFor(finding, field);
  const isChallenge = field === 'pj_category';
  const noPjGrade = isChallenge && !finding.pj_grade && !finding.pj_reason;
  const canSubmit = value.trim().length > 0 && reason.trim().length >= 3 && !saving && !noPjGrade;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    setError(null);
    trackEvent('action', next ? 'missed_order_correction_open' : 'missed_order_correction_close', {
      sales_order_no: finding.sales_order_no,
      finding_id: finding.finding_id,
      outcome: next ? 'open' : 'close',
      recorded_by_dom: true,
    });
  };

  const changeField = (next: CorrectionField) => {
    setField(next);
    setValue('');
    setError(null);
    trackEvent('action', 'missed_order_correction_field_pick', {
      sales_order_no: finding.sales_order_no,
      finding_id: finding.finding_id,
      field: next,
      outcome: 'none',
      recorded_by_dom: true,
    });
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const row = await portalMissedOrdersService.correct(finding.finding_id, {
        field,
        human_value: value.trim(),
        reason: reason.trim(),
      });
      trackEvent('action', 'missed_order_correction_submit', {
        sales_order_no: finding.sales_order_no,
        finding_id: finding.finding_id,
        correction_id: row.id,
        field,
        status: row.status,
        outcome: 'request',
      });
      onRecorded(row);
      setOpen(false);
      setValue('');
      setReason('');
    } catch (err) {
      setError(correctionErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.wrap} data-testid={`correction-control-${finding.sales_order_no}`}>
      <button
        ref={triggerRef}
        type="button"
        className={open ? styles.triggerOpen : styles.trigger}
        onClick={toggle}
        data-action="missed_order_correction_open"
        data-target-sales-order-no={finding.sales_order_no}
        data-target-finding-id={finding.finding_id}
        aria-expanded={open}
      >
        {open ? 'Cancel' : 'That is wrong'}
      </button>
      {open && createPortal(
        <form
          className={styles.form}
          style={style}
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          data-testid="correction-form"
          data-correction-for={finding.sales_order_no}
        >
          <label className={styles.label}>
            What is wrong
            <select
              className={styles.select}
              value={field}
              onChange={(e) => changeField(e.target.value as CorrectionField)}
              data-action="missed_order_correction_field_pick"
              data-target-finding-id={finding.finding_id}
            >
              {options.fields.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
          <div className={styles.current}>
            {isChallenge ? 'PJ graded it' : 'Merlin said'}: <strong>{current}</strong>
          </div>
          {isChallenge && (
            <div className={styles.challengeNote}>
              PJ's category is the source of truth. This goes to PJ as a challenge; his grade stands
              until he answers.
            </div>
          )}
          {noPjGrade && (
            <div className={styles.error}>
              This finding carries no PJ grade yet, so there is nothing to challenge. Correct
              Merlin's cause instead.
            </div>
          )}
          <label className={styles.label}>
            It was
            {choices ? (
              <select
                className={styles.select}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                data-action="missed_order_correction_value_pick"
                data-target-finding-id={finding.finding_id}
                aria-label="Correct value"
              >
                <option value="">Pick one…</option>
                {choices.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className={styles.input}
                value={value}
                maxLength={80}
                placeholder="e.g. homogenizer down"
                onChange={(e) => setValue(e.target.value)}
                aria-label="Correct value"
              />
            )}
          </label>
          <label className={styles.label}>
            Because
            <textarea
              className={styles.textarea}
              value={reason}
              maxLength={options.max_reason_chars}
              rows={3}
              placeholder="What you saw or know that Merlin did not"
              onChange={(e) => setReason(e.target.value)}
              aria-label="Reason"
            />
          </label>
          {error && (
            <div className={styles.error} role="alert">
              {error}
            </div>
          )}
          <div className={styles.actions}>
            <button
              type="submit"
              className={styles.submit}
              disabled={!canSubmit}
              data-action="missed_order_correction_submit"
              data-target-finding-id={finding.finding_id}
              data-target-sales-order-no={finding.sales_order_no}
              data-value-field={field}
            >
              {saving ? 'Saving…' : isChallenge ? 'Send challenge to PJ' : 'Record correction'}
            </button>
          </div>
        </form>,
        document.body,
      )}
    </div>
  );
}
